import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowRight } from "lucide-react";
import { scrollToId } from "../../lib/scroll";

const LINKS = [
  { label: "How it works", href: "#how-it-works" },
  { label: "Features", href: "#features" },
  { label: "Projects", href: "#projects" },
  { label: "Pricing", href: "#pricing" },
  { label: "For Business", href: "#business" },
  { label: "FAQs", href: "#faqs" },
];

export const Wordmark = ({ dark = false, className = "h-9" }) => (
  <span className="inline-flex items-center gap-2.5">
    <img src="/icon.png" alt="Deyem logo" className={`w-auto ${className}`} />
    <span
      className={`font-display font-semibold tracking-tight text-[1.55rem] leading-none ${
        dark ? "text-white" : "text-diem-ink"
      }`}
    >
      Deyem
    </span>
  </span>
);

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (e, href) => {
    e.preventDefault();
    setOpen(false);
    scrollToId(href);
  };

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow,border-color] duration-500 ${
        scrolled
          ? "bg-white/70 backdrop-blur-2xl border-b border-white/30 shadow-[0_4px_30px_rgb(0,0,0,0.05)]"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 lg:px-8 h-[72px] flex items-center justify-between">
        <a href="#top" onClick={(e) => go(e, "#top")} data-testid="nav-logo" aria-label="Deyem home">
          <Wordmark />
        </a>

        <div className="hidden lg:flex items-center gap-8">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={(e) => go(e, l.href)}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="text-sm font-semibold text-diem-slate hover:text-diem-blue transition-colors duration-300"
            >
              {l.label}
            </a>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <button
            data-testid="nav-waitlist-cta"
            onClick={(e) => go(e, "#waitlist")}
            className="group hidden sm:inline-flex items-center gap-2 rounded-full bg-diem-blue text-white text-sm font-semibold px-5 py-2.5 shadow-[0_8px_24px_rgba(0,85,255,0.35)] hover:shadow-[0_12px_32px_rgba(0,85,255,0.45)] hover:-translate-y-0.5 active:scale-[0.98] transition-[box-shadow,transform] duration-300"
          >
            Join the waitlist
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5" />
          </button>
          <button
            data-testid="nav-mobile-menu-button"
            aria-label="Open menu"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden w-10 h-10 rounded-full border border-diem-line bg-white/80 backdrop-blur flex items-center justify-center text-diem-ink"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden bg-white/95 backdrop-blur-2xl border-b border-diem-line"
          >
            <div className="px-6 py-6 flex flex-col gap-1">
              {LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={(e) => go(e, l.href)}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="font-display text-xl font-medium tracking-tight text-diem-ink py-3 border-b border-diem-line/60"
                >
                  {l.label}
                </a>
              ))}
              <button
                data-testid="mobile-nav-waitlist-cta"
                onClick={(e) => go(e, "#waitlist")}
                className="mt-5 inline-flex items-center justify-center gap-2 rounded-full bg-diem-blue text-white font-semibold px-5 py-3.5"
              >
                Join the waitlist <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
